import {Banner} from '@primer/react/experimental'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

import {STAFF_SHIP_FILE_LIMIT} from './PullRequestHeaderNavigation'
import type {FilesRoutePayload, PageLimits} from '../page-data/payloads/files'
import type {NavigationUrls} from '../types/navigation-urls-types'
import {Link} from '@primer/react'
import {toggleGenericFeaturePath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'

/**
 * Props for the PageLimitsBanner component
 */
export type PageLimitsBannerProps = {
  /**
   * Object containing information about page limits and whether they are exceeded
   * Includes limits for files, review threads, and annotations
   */
  pageLimits: PageLimits | undefined
  /**
   * Repository information including owner and name
   */
  repository: FilesRoutePayload['repository']
  /**
   * Legacy flag for showing file limits before centralized PRX limits
   * @deprecated Will be removed when the apply_prx_limits feature flag is fully rolled out
   */
  showFilesLimitDeprecated: boolean
  /**
   * Navigation URLs for the pull request, used for the "switch back" link
   */
  urls: NavigationUrls
}

/**
 * Displays a warning banner when page limits are exceeded in a pull request.
 *
 * This component handles two cases:
 * 1. When the apply_prx_limits feature flag is enabled: shows a banner when files,
 *    review threads, or annotations limits are exceeded as defined in pageLimits
 * 2. Legacy case: shows a banner based on a hardcoded STAFF_SHIP_FILE_LIMIT when
 *    showFilesLimitDeprecated is true
 *
 * The banner includes a link to switch back to the classic pull request page
 * to view all content without limits.
 */
export function PageLimitsBanner({pageLimits, repository, urls, showFilesLimitDeprecated}: PageLimitsBannerProps) {
  const prxLimitsEnabled = useFeatureFlag('apply_prx_limits')
  const pageLimitsExceeded =
    prxLimitsEnabled &&
    (pageLimits?.filesLimitExceeded || pageLimits?.reviewThreadsLimitExceeded || pageLimits?.annotationsLimitExceeded)

  const limitExceededMessage = () => {
    const messagePrefix = 'Only the first '
    const messagePostfix = ' are currently being shown.'

    const exceededLimitCounts = []
    if (pageLimits?.filesLimitExceeded) {
      exceededLimitCounts.push(`${pageLimits.filesLimit} files`)
    }
    if (pageLimits?.reviewThreadsLimitExceeded) {
      exceededLimitCounts.push(`${pageLimits.reviewThreadsLimit} comments`)
    }
    if (pageLimits?.annotationsLimitExceeded) {
      exceededLimitCounts.push(`${pageLimits.annotationsLimit} alerts`)
    }

    let formattedLimits = ''
    if (exceededLimitCounts.length === 1) {
      formattedLimits = `${exceededLimitCounts[0]}`
    } else if (exceededLimitCounts.length === 2) {
      formattedLimits = `${exceededLimitCounts[0]} and ${exceededLimitCounts[1]}`
    } else if (exceededLimitCounts.length === 3) {
      formattedLimits = `${exceededLimitCounts[0]}, ${exceededLimitCounts[1]}, and ${exceededLimitCounts[2]}`
    }

    return messagePrefix + formattedLimits + messagePostfix
  }

  async function handleFeaturePreviewToggle() {
    const formData = new FormData()
    formData.append('feature_name', 'prx_files')

    await verifiedFetch(toggleGenericFeaturePath({repo: repository}), {
      body: formData,
      method: 'POST',
    })
  }

  const warningDescription = prxLimitsEnabled ? (
    <>
      {limitExceededMessage()} To see more,{' '}
      <Link inline href={`${urls.files}?new_files_changed=false`} onClick={handleFeaturePreviewToggle} rel="noreferrer">
        switch back
      </Link>{' '}
      to the classic page.
    </>
  ) : (
    <>
      Only the first {STAFF_SHIP_FILE_LIMIT} files are currently being shown. To see more changes,{' '}
      <Link inline href={`${urls.files}?new_files_changed=false`} onClick={handleFeaturePreviewToggle} rel="noreferrer">
        switch back
      </Link>{' '}
      to the classic page.
    </>
  )

  if (!showFilesLimitDeprecated && !prxLimitsEnabled) return null
  if (prxLimitsEnabled && !pageLimitsExceeded) return null

  return (
    <Banner
      aria-label="Warning"
      title="Warning"
      variant="warning"
      hideTitle
      description={warningDescription}
      className="mb-3"
    />
  )
}

try{ PageLimitsBanner.displayName ||= 'PageLimitsBanner' } catch {}